/**
 * Konflikte ansehen und entscheiden (PV-B14).
 *
 * Bis hierher gab es die Zahl in der Statusleiste — und sonst nichts. Kein Ort,
 * an dem man sah, worum es geht, und keinen Weg, es zu erledigen. Wer drei
 * offene Änderungen hatte, konnte abgleichen so oft er wollte: Es blieben drei,
 * und die Konfliktzahl wuchs bei jedem Versuch weiter. Ein Angebot ließ sich
 * damit nicht erzeugen, weil das offene Änderungen voraussetzt, die es nicht
 * mehr gibt.
 *
 * Entschieden wird je Datensatz, mit zwei Knöpfen. Automatisch zusammenführen
 * wäre hier falsch: Ob die Neigung vom Dach 30 oder 35 Grad hat, kann kein
 * Programm entscheiden — das weiß nur, wer oben war.
 */

import { useLiveQuery } from 'dexie-react-hooks';

import { db } from '../daten/db.ts';
import { entscheideKonflikt } from '../daten/abgleich.ts';
import type { Konflikt } from '../typen.ts';

const TABELLENNAMEN: Record<string, string> = {
  projekte: 'Projekt',
  flaechen: 'Fläche',
  belegungen: 'Belegung',
  fotos: 'Foto',
  strings: 'String',
  projekt_komponenten: 'Komponente',
  leitungswege: 'Leitungsweg',
  materialpositionen: 'Materialposition',
  wirtschaftlichkeit: 'Wirtschaftlichkeit',
  verschattungsobjekte: 'Verschattungsobjekt',
  kartenmarken: 'Bemaßung oder Markierung',
  auftraege: 'Auftrag',
  pruefbefunde: 'Prüfbefund',
  projekteinstellungen: 'Projekteinstellung',
  einstellungen: 'Einstellung',
  module: 'Modul',
  komponenten: 'Komponente',
  artikel: 'Artikel',
  materialsaetze: 'Materialsatz',
};

/**
 * Woran man den Datensatz erkennt. Ohne das stand dort nur "Komponente", und
 * bei zwei Zeilen mit unterschiedlichem Preis war nicht zu sehen, welche
 * gemeint ist.
 */
function bezeichnung(konflikt: Konflikt): string {
  const wert = (feld: string): string | null => {
    const a = konflikt.meine[feld] ?? konflikt.server[feld];
    return typeof a === 'string' && a.trim() ? a : null;
  };
  const teile = [
    wert('hersteller'),
    wert('typ'),
    wert('bezeichnung'),
    wert('name'),
    wert('kunde_name'),
    wert('nummer'),
    wert('schluessel'),
  ].filter(Boolean);
  return teile.length > 0 ? teile.join(' ') : '';
}

/**
 * Stammdaten gelten fuer alle Projekte, nicht nur fuer das offene.
 *
 * Der Unterschied ist bei einem Preiskonflikt der entscheidende: Wer hier
 * "meine Fassung" waehlt, aendert die Preisliste der Firma — fuer jedes
 * kuenftige Angebot und fuer jeden Kollegen. Fuer einen Sonderpreis in genau
 * einem Projekt gibt es den anderen Weg, und darauf muss hier hingewiesen
 * werden, bevor jemand die Liste umschreibt.
 */
const STAMMDATEN = new Set(['module', 'komponenten', 'artikel', 'materialsaetze', 'einstellungen']);

/**
 * Spaltennamen sind fuer die Datenbank geschrieben, nicht fuer den Bildschirm.
 *
 * In der Konflikttabelle standen sie bisher roh da — "flaeche_id", "hoehe_m",
 * "laenge_m". Wer entscheiden soll, welche Fassung gilt, liest dort also
 * Feldnamen ohne Umlaute und mit Einheiten im Bezeichner. Was hier fehlt,
 * faellt auf den Spaltennamen zurueck; das ist besser als eine Luecke.
 */
const FELDNAMEN: Record<string, string> = {
  // Projekt
  name: 'Name',
  kunde_name: 'Kunde',
  kunde_email: 'E-Mail',
  kunde_telefon: 'Telefon',
  kunde_geburtsdatum: 'Geburtsdatum',
  stromlieferant: 'Stromlieferant',
  stromabnehmer: 'Stromabnehmer',
  einspeisebegrenzung_kw: 'Einspeisebegrenzung (kW)',
  strasse: 'Straße',
  plz: 'PLZ',
  ort: 'Ort',
  lat: 'Breitengrad',
  lng: 'Längengrad',
  status: 'Status',
  notizen: 'Notizen',
  netzbetreiber: 'Netzbetreiber',
  zaehlpunktnummer: 'Zählpunktnummer',
  zaehlerart: 'Zählerart',
  einspeiseart: 'Einspeiseart',
  einspeisepunkt: 'Einspeisepunkt',
  inbetriebnahme: 'Inbetriebnahme',
  strahlungsdaten: 'Strahlungsdaten',

  // Fläche
  art: 'Art',
  polygon: 'Umriss',
  aussparungen: 'Hindernisse',
  traufkante_index: 'Traufkante',
  neigung_grad: 'Neigung (Grad)',
  azimut_berechnet: 'Azimut gerechnet (Grad)',
  azimut_ueberschrieben: 'Azimut überschrieben (Grad)',
  referenzmass: 'Referenzmaß',
  tragwerk: 'Sparren und Pfetten',
  regeln: 'Belegungsregeln',
  aufstaenderung_grad: 'Aufständerung (Grad)',
  unterkante_m: 'Unterkante (m)',
  dachtyp: 'Dachtyp',
  ausrichtung: 'Ausrichtung',
  aus_karte: 'Aus der Karte übernommen',

  // Belegung
  modul_id: 'Modul',
  entfernte_plaetze: 'Entfernte Plätze',
  modulfeld: 'Modulfeld',

  // Abwicklung
  schritt: 'Schritt',
  erledigt_am: 'Erledigt am',
  erledigt_von: 'Erledigt von',
  entfaellt: 'Entfällt',
  erinnern_am: 'Erinnern am',
  notiz: 'Notiz',
  mime: 'Dateityp',
  angelegt_von: 'Angelegt von',

  // Modul und Komponenten
  hersteller: 'Hersteller',
  typ: 'Typ',
  breite_mm: 'Breite (mm)',
  hoehe_mm: 'Höhe (mm)',
  tiefe_mm: 'Tiefe (mm)',
  gewicht_kg: 'Gewicht (kg)',
  nennleistung_wp: 'Nennleistung (Wp)',
  uoc_v: 'Leerlaufspannung U_oc (V)',
  umpp_v: 'MPP-Spannung U_mpp (V)',
  impp_a: 'MPP-Strom I_mpp (A)',
  tk_uoc_prozent_k: 'Temperaturkoeffizient U_oc (%/K)',
  modulart: 'Modulart',
  isc_a: 'Kurzschlussstrom I_sc (A)',
  tk_isc_prozent_k: 'Temperaturkoeffizient Strom (%/K)',
  max_systemspannung_v: 'Max. Systemspannung (V)',
  datenblatt_id: 'Datenblatt',
  ist_platzhalter: 'Platzhalter',
  kenndaten: 'Kenndaten',
  mppt: 'MPP-Tracker',
  phasen: 'Phasen',
  wechselrichter_id: 'Wechselrichter',
  komponente_id: 'Komponente',

  // Artikel und Material
  nummer: 'Nummer',
  bezeichnung: 'Bezeichnung',
  einheit: 'Einheit',
  warengruppe: 'Warengruppe',
  einkaufspreis: 'Einkaufspreis',
  einzelpreis: 'Einzelpreis',
  aufschlag_prozent: 'Aufschlag (%)',
  artikel_id: 'Artikel',
  menge: 'Menge',
  anzahl: 'Anzahl',
  je_modul: 'Je Modul',
  je_flaeche: 'Je Fläche',
  montage: 'Montagesystem',
  preis_stand: 'Preisstand',
  preise: 'Preise',
  kategorie: 'Kategorie',
  bemerkung: 'Bemerkung',
  punkte: 'Lage auf der Karte',
  text: 'Text',
  bemerkungen: 'Bemerkungen',
  beschreibung: 'Beschreibung',
  wert: 'Wert',
  schluessel: 'Schlüssel',

  // Leitungsweg
  von: 'Von',
  nach: 'Nach',
  laenge_m: 'Länge (m)',
  querschnitt_mm2: 'Querschnitt (mm²)',
  zuleitung_m: 'Zuleitung (m)',
  verlegeart: 'Verlegeart',
  spannung_v: 'Spannung (V)',
  angaben: 'Angaben',

  // Verschattung
  grundflaeche: 'Grundfläche',
  hoehe_ueber_grund_m: 'Höhe über Grund (m)',
  hoehe_m: 'Höhe (m)',

  // Wirtschaftlichkeit
  jahresverbrauch_kwh: 'Jahresverbrauch (kWh)',
  monatswerte_kwh: 'Monatswerte (kWh)',
  speicher_kwh: 'Speicher (kWh)',
  foerderung_euro: 'Förderung (EUR)',
  zusatzverbraucher: 'Zusatzverbraucher',
  zusatzmodule: 'Zusatzmodule',
  sonnenstand_grad: 'Sonnenstand (Grad)',

  // Auftrag und Prüfbefund
  auftragsart: 'Auftragsart',
  montage_von: 'Montage von',
  montage_bis: 'Montage bis',
  monteur: 'Monteur',
  checkliste: 'Checkliste',
  abgenommen_am: 'Abgenommen am',
  pruefer: 'Prüfer',
  geprueft_am: 'Geprüft am',
  messwerte: 'Messwerte',
  unterschrift_name: 'Unterschrift',
  module: 'Module',
  farbe: 'Farbe',
  fassade: 'Fassade',

  // Foto
  dateiname: 'Dateiname',
  groesse_bytes: 'Größe (Bytes)',
  breite_px: 'Breite (Pixel)',
  hoehe_px: 'Höhe (Pixel)',
  aufgenommen_am: 'Aufgenommen am',
  aktiv: 'Aktiv',
};

/** Felder, die niemanden interessieren, wenn er entscheiden soll. */
const VERBORGEN = new Set([
  'id',
  'seq',
  'schmutzig',
  'geaendert_am',
  'angelegt_am',
  'projekt_id',
  'flaeche_id',
  'bearbeiter_id',
  'bearbeiter_geraet',
  'sperre_seit',
]);

/** Was sich zwischen den beiden Fassungen tatsächlich unterscheidet. */
function unterschiede(konflikt: Konflikt): Array<{ feld: string; meine: string; server: string }> {
  const felder = new Set([...Object.keys(konflikt.meine), ...Object.keys(konflikt.server)]);
  const zeilen: Array<{ feld: string; meine: string; server: string }> = [];
  for (const feld of felder) {
    if (VERBORGEN.has(feld)) continue;
    const a = alsText(konflikt.meine[feld]);
    const b = alsText(konflikt.server[feld]);
    if (a !== b) zeilen.push({ feld, meine: a, server: b });
  }
  return zeilen;
}

function alsText(wert: unknown): string {
  if (wert === null || wert === undefined) return '—';
  if (typeof wert === 'object') {
    const text = JSON.stringify(wert);
    return text.length > 80 ? `${text.slice(0, 77)}…` : text;
  }
  return String(wert);
}

export function Konflikte({ onSchliessen }: { onSchliessen: () => void }): React.JSX.Element {
  const konflikte = useLiveQuery(() => db.konflikte.toArray(), [], [] as Konflikt[]);

  return (
    <div className="druckrahmen" onClick={onSchliessen}>
      <div
        className="druckblatt"
        style={{ maxWidth: '48rem' }}
        onClick={(ereignis) => ereignis.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
          <div style={{ flex: 1 }}>
            <p className="marke">Abgleich</p>
            <h1>Konflikte</h1>
          </div>
          <button onClick={onSchliessen}>Schließen</button>
        </div>

        {konflikte.length === 0 ? (
          <div className="hinweis hinweis--gut">
            Keine offenen Konflikte. Alles, was auf diesem Gerät geändert wurde, ist übertragen.
          </div>
        ) : (
          <>
            <p className="klein">
              Dieselbe Zeile wurde hier und woanders geändert. Solange nicht entschieden ist, bleibt
              die Änderung in der Warteschlange — und der Abgleich meldet weiter offene Punkte.
            </p>

            {konflikte.map((konflikt) => {
              const zeilen = unterschiede(konflikt);
              const istStammdaten = STAMMDATEN.has(konflikt.tabelle);
              return (
                <div key={konflikt.id} className="kachel">
                  <h3 style={{ marginTop: 0 }}>
                    {TABELLENNAMEN[konflikt.tabelle] ?? konflikt.tabelle}
                    {bezeichnung(konflikt) ? ` „${bezeichnung(konflikt)}“` : ''}
                  </h3>

                  {konflikt.art === 'abgewiesen' ? (
                    <div className="hinweis hinweis--fehler">
                      Der Server nimmt diese Änderung nicht an: {konflikt.grund ?? 'ohne Angabe'}.
                      Erneutes Senden hilft nicht.
                    </div>
                  ) : zeilen.length === 0 ? (
                    <p className="klein">
                      Die Zeile wurde woanders gespeichert, inhaltlich gibt es aber keinen
                      Unterschied. „Stand vom Server“ räumt das auf.
                    </p>
                  ) : (
                    <div className="tabellenrahmen">
                      <table>
                        <thead>
                          <tr>
                            <th>Feld</th>
                            <th>Hier auf dem Gerät</th>
                            <th>Am Server</th>
                          </tr>
                        </thead>
                        <tbody>
                          {zeilen.map((zeile) => (
                            <tr key={zeile.feld}>
                              <td>{FELDNAMEN[zeile.feld] ?? zeile.feld}</td>
                              <td>
                                <strong>{zeile.meine}</strong>
                              </td>
                              <td>{zeile.server}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {istStammdaten && konflikt.art !== 'abgewiesen' && (
                    <div className="hinweis hinweis--warnung">
                      Das ist ein <strong>Stammdatensatz</strong> — er gilt für alle Projekte und
                      alle Kollegen, und der Serverstand bleibt maßgeblich. Von hier aus lässt er
                      sich nicht überschreiben.
                      <br />
                      Ist der Preis der Firma falsch, gehört er in{' '}
                      <strong>Einstellungen → Artikel und Preise</strong> korrigiert — dort, bewusst
                      und an einer Stelle. Braucht nur dieses eine Projekt einen anderen Preis
                      (Restposten, Sonderkondition, Kunde stellt bei): Projekt öffnen, dann
                      Einstellungen → Artikel und Preise → <strong>„Nur für dieses Projekt“</strong>.
                    </div>
                  )}

                  <div className="knopfreihe" style={{ marginTop: '0.6rem', marginBottom: 0 }}>
                    {/* Bei Stammdaten gibt es diesen Weg bewusst nicht: Der
                        Serverstand bleibt maßgeblich. */}
                    {konflikt.art !== 'abgewiesen' && !istStammdaten && (
                      <button
                        className="wichtig"
                        onClick={() => void entscheideKonflikt(konflikt.id!, 'meine')}
                        title="Die Fassung dieses Geräts setzt sich durch und wird beim nächsten Abgleich übertragen"
                      >
                        Meine Fassung gilt
                      </button>
                    )}
                    <button
                      onClick={() => void entscheideKonflikt(konflikt.id!, 'server')}
                      className={istStammdaten ? 'wichtig' : ''}
                      title="Die eigene Änderung fällt weg, der Serverstand wird übernommen"
                    >
                      Stand vom Server übernehmen
                    </button>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}
